/**
 * KFC-565: messages shown after the recipes calling a reusable step have been rebuilt by the
 * server (POST /recipes/rebuildAfterReusableStepUpdate).
 */

export const REBUILD_ERROR_MESSAGE = "L'étape réutilisable est enregistrée, mais la mise à jour des recettes a échoué. Relancez-la depuis le menu de l'étape réutilisable."

// reasons sent by the server (issueTypes, kfc-server utils/recipeRebuildUtils.js)
const REBUILD_REASON_LABELS = {
    SECTION_OUTPUT_NOT_HANDLED: "Division des Sauces",
    UNRESOLVED_PRIOR_STEP: "étape antérieure introuvable",
    MISSING_REUSABLE_STEP: "étape réutilisable introuvable",
    EMPTY_REUSABLE_STEP: "étape réutilisable sans poids",
    MISSING_TARGET_WEIGHT: "poids cible de l'étape réutilisable manquant",
    TRANSFORMATION_MODE_RESET: "mode de transformation non paramétré pour le nouvel article"
}

const TOO_MANY_RECIPES_CONTACT = "Rapprochez-vous de supports-KFC."

/**
 * Message shown when the server check blocks the save: the recipes calling the reusable step are
 * too many to be updated in one request.
 * @param {{recipesCount: number, limit: number}} tooManyRecipes
 */
export const getTooManyRecipesBlockedMessage = ({ recipesCount, limit }) =>
    `Étape réutilisable non enregistrée : elle est utilisée par ${recipesCount} recettes, trop pour les mettre à jour automatiquement (${limit} au plus). ${TOO_MANY_RECIPES_CONTACT}`

/**
 * Same, when the reusable step is already saved (check unavailable, or update relaunched from the menu).
 * @param {{recipesCount: number, limit: number}} tooManyRecipes
 */
export const getTooManyRecipesNotUpdatedMessage = ({ recipesCount, limit }) =>
    `L'étape réutilisable est enregistrée, mais ses ${recipesCount} recettes n'ont pas été mises à jour : trop de recettes pour une mise à jour automatique (${limit} au plus). ${TOO_MANY_RECIPES_CONTACT}`

export const getRebuildSuccessMessage = ({ recipesUpdated = [] }) => {
    const count = recipesUpdated.length
    if (!count) return "Étape réutilisable enregistrée"
    return count === 1
        ? "Étape réutilisable enregistrée : 1 recette mise à jour"
        : `Étape réutilisable enregistrée : ${count} recettes mises à jour`
}

export const hasRecipesToReview = ({ recipesToUpdateManually = [], recipesFailed = [] }) => recipesToUpdateManually.length > 0 || recipesFailed.length > 0

export const getRebuildReasonsLabel = (reasons = []) => reasons.map(reason => REBUILD_REASON_LABELS[reason] || reason).join(", ")

export const getRecipeProductionStepsUrl = (recipeId) => `/products/recipe/productionSteps/${recipeId}`

const getRecipesCountLabel = (count) => count === 1 ? "1 recette" : `${count} recettes`

/**
 * Message shown when the server check (POST /recipes/checkReusableStepUpdate) blocks the save: a
 * supplier item replaced in the reusable step does not support a transformation mode the recipes
 * apply to it.
 * @param {Array<{supplierItem: {name: string}, transformationMode: {objectId: string, name: string}, recipes: Array}>} conflicts
 * @param {Array<{objectId: string, name: string}>} [transformationModes] current modes: the recipe copies may keep an older name
 */
export const getTransformationModeConflictsMessage = (conflicts, transformationModes = []) => {
    const getModeName = (transformationMode) => {
        const currentMode = transformationModes.find(mode => mode.objectId === transformationMode.objectId)
        return currentMode ? currentMode.name : transformationMode.name
    }
    const instruction = conflicts.length === 1
        ? "Paramétrez ce mode de transformation sur l'article"
        : "Paramétrez ces modes de transformation sur les articles"
    const details = conflicts
        .map(({ supplierItem, transformationMode, recipes }) => `mode « ${getModeName(transformationMode)} » sur « ${supplierItem.name} » (${getRecipesCountLabel(recipes.length)})`)
        .join(", ")

    return `Étape réutilisable non enregistrée. ${instruction}, puis enregistrez à nouveau : ${details}`
}

/**
 * Supplier items of the reusable step without gross weight, as left when a supplier item is replaced:
 * saved as is, the reusable step would update its recipes with that ingredient at 0 g.
 * @param {Array<Object>} productionSteps production steps of the reusable step form
 * @returns {Array<string>} supplier item names
 */
export const getIngredientsWithoutWeight = (productionSteps = []) => productionSteps
    .flatMap(productionStep => productionStep.stepComponents || [])
    .filter(stepComponent => stepComponent.supplierItem && !(Number(stepComponent.grossWeight) > 0))
    .map(stepComponent => stepComponent.supplierItem.name || stepComponent.supplierItem.objectId)

/**
 * @param {Array<string>} supplierItemNames
 */
export const getMissingWeightMessage = (supplierItemNames) =>
    `Étape réutilisable non enregistrée : saisissez le poids de ${supplierItemNames.map(name => `« ${name} »`).join(", ")}, puis enregistrez à nouveau.`
